<template>
    <modal :title="stage.title" class="custom-work-modal" @dismiss="dismiss">
        <div class="custom-work-order-flow loading-container">
            <CustomWorkOptions
                :packages="packages"
                @packageSelected="packageSelected"
                v-if="stage.key === 'packageSelection'"
            />
            <CustomWorkExecutivePicker
                v-else-if="stage.key === 'executiveSelection'"
                v-model="executives"
                :selectedPackage="selectedPackage"
            />
            <CustomWorkConfirmation
                v-else-if="stage.key === 'confirmation'"
                :executives="executives"
                :packageName="selectedPackage.name"
                :packagePrice="selectedPackage.price"
                :quantity="executives.length"
                :errors="errors"
            />
            <CustomWorkConfirmed v-else-if="stage.key === 'confirmed'" :order="order" />
            <div class="footer">
                <SalesContactInfo />

                <div class="button-group">
                    <button class="primary" @click="previous" v-if="hasPrevious">
                        <i class="material-icons">chevron_left</i>
                        Back
                    </button>
                    <button class="primary" @click="next" v-if="hasNext" :disabled="!nextEnabled">
                        {{ stage.next }}
                    </button>
                </div>
            </div>
            <loading :active="updating || ordering" :is-full-page="false" />
        </div>
    </modal>
</template>
<script setup>
import { CustomWorkService } from '@/services/customWorkService'
import CustomWorkOptions from './CustomWorkOptions'
import CustomWorkExecutivePicker from './CustomWorkExecutivePicker'
import CustomWorkConfirmation from './CustomWorkConfirmation'
import CustomWorkConfirmed from './CustomWorkConfirmed'
import SalesContactInfo from './SalesContactInfo'
import { computed, inject, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'

const props = defineProps({
    initialPackage: {
        type: Object,
    },
    initialExecutives: {
        type: Array,
        default() {
            return []
        },
    },
})

const executives = ref(props.initialExecutives ?? [])
const errors = ref({})
const order = ref(null)
const ordering = ref(false)
const selectedPackage = ref(props.initialPackage ?? null)

watch(
    () => props.initialExecutives,
    () => (executives.value = props.initialExecutives ?? [])
)
watch(
    () => props.initialPackage,
    () => {
        selectedPackage.value = props.initialPackage ?? null

        if (!selectedPackage.value) {
            stage.value = stages[0]
        } else {
            stage.value = stages[1]
        }
    }
)

const configuration = computed(() => store.getters.customWork)
const hasNext = computed(() => !!stage.value.next)
const hasPrevious = computed(() => stage.value.previous)
const updating = computed(() => configuration.value.loading)
const nextEnabled = computed(() => stage.value.nextEnabled(this))
const packages = computed(() => configuration.value.packages ?? [])

function dismiss() {
    store.getters.customWork.hide()
}

const store = inject('store')

function next() {
    if (stage.value.nextAction) {
        stage.value.nextAction()
    } else {
        stage.value = stages[stages.indexOf(stage.value) + 1]
    }
}
function previous() {
    stage.value = stages[stages.indexOf(stage.value) - 1]
}

function packageSelected(option) {
    selectedPackage.value = option
    next()
}

const bus = inject('bus')
const flash = inject('flash')

async function placeOrder() {
    ordering.value = true

    const { ok, content } = await CustomWorkService.create({
        package: selectedPackage.value.key,
        price: selectedPackage.value.price * executives.value.length,
        executives: executives.value.map(e => ({
            executiveId: e.id,
            executiveName: e.colloquialName,
            executiveTitle: e.position,
            company: e.currentCompany?.name,
            symbol: e.currentCompany?.symbol,
        })),
    })

    ordering.value = false

    if (!ok) {
        flash('Failed to place order', 'error')
        return
    }

    order.value = content

    bus.publish('custom-work-ordered', content)
    stage.value = stages[stages.indexOf(stage.value) + 1]
}

const router = useRouter()

function navigateToCustomWork() {
    router.push('/my-custom-work/')
    dismiss()
}

const stages = [
    {
        key: 'packageSelection',
        next: null,
        previous: false,
        title: 'Choose Custom Work Package',
        nextEnabled() {
            return true
        },
    },
    {
        key: 'executiveSelection',
        next: 'Next',
        previous: true,
        title: 'Choose Executives',
        nextEnabled() {
            return executives.value.length > 0
        },
    },
    {
        key: 'confirmation',
        next: 'Confirm',
        previous: true,
        title: 'Confirm Your Order',
        nextEnabled() {
            return selectedPackage.value != null && executives.value.length > 0
        },
        nextAction: placeOrder,
    },
    {
        key: 'confirmed',
        next: 'View My Custom Work',
        previous: false,
        title: 'Order Placed',
        nextEnabled() {
            return selectedPackage.value != null && executives.value.length > 0
        },
        nextAction: navigateToCustomWork,
    },
]

const stage = ref(props.initialPackage ? stages[1] : stages[0])
</script>
<style>
.custom-work-modal {
    white-space: initial;

    .modal {
        max-width: 1080px;
        min-width: 480px;
        width: 1080px;
    }

    .modal-body {
        display: flex;
        flex-direction: column;
        gap: 16px;
        justify-content: space-between;
    }

    .modal-footer {
        display: none;
    }
}

.custom-work-order-flow {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 496px;
    justify-content: space-between;

    & > * {
        flex-grow: 1;
    }

    .footer {
        align-items: center;
        display: flex;
        height: 56px;
        flex-grow: 0;

        .button-group {
            flex-grow: 1;
            margin: 0;
        }
    }
}
</style>
